import { ListItem, ListItemText } from '@mui/material';
import dayjs from 'dayjs';

import { Ticket } from '../MessageList.interface';

import TitleSection from './TitleSection';
import DescriptionSection from './DescriptionSection';

import useUpdateTicket from 'services/help-center/mutations/useUpdateTicket';
import { LAST_VIEWED_FIELD_ID } from 'services/help-center/constants';

export default function MessageItem({
    ticket,
    onSelect = () => {},
}: {
    ticket: Ticket;
    onSelect?: (ticket: Ticket) => void;
}) {
    const updatedDateTimestamp = dayjs().utc().format('YYYY-MM-DDTHH:mm:ss[Z]');

    const { mutate: updateTicket } = useUpdateTicket();

    const handleSelect = () => {
        const lastTimeViewed = ticket.custom_fields?.find((field) => field.id === LAST_VIEWED_FIELD_ID)?.value;
        const isLastViewedPastFiveMinutes = lastTimeViewed
            ? dayjs(lastTimeViewed).utc().isBefore(dayjs().utc().subtract(5, 'minutes'))
            : true;
        const isUpdatedAfterViewed = ticket.updated_at
            ? dayjs(ticket.updated_at).utc().isAfter(dayjs(lastTimeViewed).utc())
            : false;

        // If the last viewed time is within the last 5 minutes, don't update the timestamp
        if (!isLastViewedPastFiveMinutes && !isUpdatedAfterViewed) {
            onSelect?.(ticket);
            return;
        }

        if (ticket.status !== 'closed') {
            updateTicket({
                ticketId: ticket.id,
                payload: {
                    ticket: {
                        custom_fields: [{ id: LAST_VIEWED_FIELD_ID, value: updatedDateTimestamp }],
                    },
                },
            });
        }

        onSelect?.(ticket);
    };

    return (
        <ListItem
            onClick={handleSelect}
            divider
            sx={{
                cursor: 'pointer',
                paddingLeft: 2.75,
                paddingY: 3,
                transition: 'background-color .20s ease-in-out',
                '&:first-of-type': {
                    paddingTop: 0,
                },
                '&:hover, &.Mui-focusVisible, &.Mui-selected': {
                    backgroundColor: 'transparent',
                },
            }}>
            <ListItemText
                primary={<TitleSection ticket={ticket} />}
                secondary={<DescriptionSection ticket={ticket} />}
            />
        </ListItem>
    );
}
